
import React from "react"

import { cn, removeFontSizeClasses, generateResponsiveFontStyles } from '@/lib/utils';
import { TruncatedTextWithTooltip } from "@/components/ui/truncated-text-with-tooltip"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("bg-muted border-b border-border text-foreground rounded-t-lg px-4 py-3 flex flex-col gap-0", className)} {...props} />
))
CardHeader.displayName = "CardHeader"

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  icon?: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
}

const CardTitle = React.forwardRef<
  HTMLDivElement,
  CardTitleProps
>(({ className, children, style, icon, subtitle, actions, ...props }, ref) => {
  // Se children é uma string, aplica padronização completa
  let processedChildren = children;
  let responsiveStyles: React.CSSProperties = {};
  let originalText = '';

  if (typeof children === 'string') {
    originalText = children;
    const upperText = children.toUpperCase();
    processedChildren = upperText;
    responsiveStyles = generateResponsiveFontStyles(upperText);
  }

  // Remove qualquer classe de tamanho de fonte do className fornecido
  const cleanedClassName = removeFontSizeClasses(className || '');

  // Combina os estilos responsivos com os estilos fornecidos
  const combinedStyles = { ...responsiveStyles, ...style };

  // Layout simplificado
  return (
    <div ref={ref} className="w-full flex items-center justify-between">
      <div className="flex-1 min-w-0">
        {typeof children !== 'string' || !children.trim() ? (
          <h3
            className={cn(
              'text-base font-semibold leading-tight tracking-tight text-left',
              cleanedClassName
            )}
            style={combinedStyles}
            {...props}
          >
            {processedChildren}
          </h3>
        ) : (
          <TruncatedTextWithTooltip text={originalText}>
            {({ ref: tooltipRef, isTruncated }) => (
              <h3
                ref={(node) => {
                  if (typeof ref === 'function') {
                    ref(node);
                  } else if (ref) {
                    ref.current = node;
                  }
                  (tooltipRef as React.MutableRefObject<HTMLElement | null>).current = node;
                }}
                className={cn(
                  'text-base font-semibold leading-tight tracking-tight text-left',
                  cleanedClassName
                )}
                style={combinedStyles}
                {...props}
              >
                {processedChildren}
              </h3>
            )}
          </TruncatedTextWithTooltip>
        )}
      </div>

      {/* Área dos ícones */}
      {(icon || actions) && (
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {icon && <div>{icon}</div>}
          {actions && <div>{actions}</div>}
        </div>
      )}
    </div>
  );
})
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground text-left mt-0.5", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-4 py-4", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center px-4 py-4", className)} {...props} />
))
CardFooter.displayName = "CardFooter"

const CardAction = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center justify-end px-4", className)} {...props} />
))
CardAction.displayName = "CardAction"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, CardAction }
